export type CustomerOrderSelection = {
  storeId: string;
  sourceStoreId: string;
  platform: string;
};

type PreferenceStorage = Pick<Storage, "getItem" | "setItem">;
const defaultSelection: CustomerOrderSelection = { storeId: "all", sourceStoreId: "all", platform: "all" };

function browserStorage(): PreferenceStorage | null {
  try { return typeof window === "undefined" ? null : window.localStorage; }
  catch { return null; }
}

function preferenceKey(accountId: string) {
  return accountId.trim() ? `kenpost.customerOrders.selection.v1.${encodeURIComponent(accountId.trim())}` : "";
}

function identifier(value: unknown, fallback = "all") {
  return typeof value === "string" && value.trim().length > 0 && value.trim().length <= 160 && !/[\u0000-\u001f]/.test(value)
    ? value.trim() : fallback;
}

function normalizeSelection(value: unknown): CustomerOrderSelection {
  if (!value || typeof value !== "object" || Array.isArray(value)) return { ...defaultSelection };
  const row = value as Partial<CustomerOrderSelection>;
  const storeId = identifier(row.storeId);
  return {
    storeId,
    sourceStoreId: storeId === "all" ? "all" : identifier(row.sourceStoreId),
    platform: typeof row.platform === "string" && /^[a-z][a-z0-9_]{0,39}$/.test(row.platform) ? row.platform : "all",
  };
}

export function readCustomerOrderSelection(accountId: string, storage = browserStorage()): CustomerOrderSelection {
  try {
    const key = preferenceKey(accountId);
    return normalizeSelection(key && storage ? JSON.parse(storage.getItem(key) || "null") : null);
  } catch { return { ...defaultSelection }; }
}

export function persistCustomerOrderSelection(accountId: string, selection: CustomerOrderSelection, storage = browserStorage()) {
  try {
    const key = preferenceKey(accountId);
    if (key && storage) storage.setItem(key, JSON.stringify(normalizeSelection(selection)));
  } catch { /* A blocked browser store must not interrupt the current selection. */ }
}

export function initialCustomerOrderSelection(saved: CustomerOrderSelection, params: URLSearchParams): CustomerOrderSelection {
  const explicitStore = params.has("storeId");
  const storeId = explicitStore ? identifier(params.get("storeId")) : saved.storeId;
  return {
    storeId,
    sourceStoreId: storeId === "all" ? "all" : params.has("sourceStoreId")
      ? identifier(params.get("sourceStoreId")) : storeId === saved.storeId ? saved.sourceStoreId : "all",
    // A direct store link must not inherit an incompatible platform filter.
    platform: explicitStore ? "all" : saved.platform,
  };
}

export function resolveCustomerOrderSelection(selection: CustomerOrderSelection, stores: Array<{ id: string }>): CustomerOrderSelection {
  if (stores.some(store => store.id === selection.storeId)) return selection;
  if (selection.storeId === "all" && stores.length !== 1) return selection;
  return { storeId: stores.length === 1 ? stores[0].id : "all", sourceStoreId: "all", platform: "all" };
}
